import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { DashboardProductCategory } from '../types/myaccount';
import MyAccountCard from './MyAccountCard';

interface ProductCategoriesProps {
    title?: string;
    hiddenCategories?: string;
    whitelistedCategories?: string;
}

const parseCategoryFilterTokens = (value: string): Set<string> => {
    return new Set(
        String(value || '')
            .split(/[\n,]+/)
            .map((token) => token.trim().toLowerCase())
            .filter(Boolean)
    );
};

const matchesCategoryFilter = (category: DashboardProductCategory, filters: Set<string>): boolean => {
    if (filters.size === 0) {
        return false;
    }

    return filters.has(String(category.id)) || filters.has(category.slug.toLowerCase());
};

const ProductCategories: React.FC<ProductCategoriesProps> = ({
    title = 'Product Categories',
    hiddenCategories = '',
    whitelistedCategories = '',
}) => {
    const [categories, setCategories] = useState<DashboardProductCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hiddenCategoryFilters = useMemo(() => parseCategoryFilterTokens(hiddenCategories), [hiddenCategories]);
    const whitelistedCategoryFilters = useMemo(() => parseCategoryFilterTokens(whitelistedCategories), [whitelistedCategories]);
    const hasActiveFilters = whitelistedCategoryFilters.size > 0 || hiddenCategoryFilters.size > 0;
    const renderSkeletonGrid = () => (
        <div className="row g-3">
            {Array.from({ length: 6 }).map((_, index) => (
                <div key={`product-category-skeleton-${index}`} className="col-md-4">
                    <div className="skeleton" style={{ height: '120px', borderRadius: '8px' }} />
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        fetchCategories();
    }, [hiddenCategories, whitelistedCategories]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            setError(null);

            const dashboardData = await api.getAccountDashboardData();
            const mappedCategories = Array.isArray(dashboardData.product_categories)
                ? dashboardData.product_categories
                : [];

            const filteredCategories = whitelistedCategoryFilters.size > 0
                ? mappedCategories.filter((category) => matchesCategoryFilter(category, whitelistedCategoryFilters))
                : mappedCategories.filter((category) => !matchesCategoryFilter(category, hiddenCategoryFilters));

            setCategories(filteredCategories);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load product categories');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <MyAccountCard
                slug="product-categories"
                title={title}
                className="product-categories"
            >
                {renderSkeletonGrid()}
            </MyAccountCard>
        );
    }

    if (error) {
        return (
            <MyAccountCard
                slug="product-categories"
                title={title}
                className="product-categories"
            >
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
                <button onClick={fetchCategories} className="btn btn-primary btn-sm">
                    Retry
                </button>
            </MyAccountCard>
        );
    }

    if (categories.length === 0) {
        return (
            <MyAccountCard
                slug="product-categories"
                title={title}
                className="product-categories"
            >
                <p className="text-muted">
                    {hasActiveFilters ? 'No categories matched the current widget filters.' : 'No categories found.'}
                </p>
            </MyAccountCard>
        );
    }

    return (
        <MyAccountCard
            slug="product-categories"
            title={title}
            className="product-categories"
        >
            <div className="row g-3">
                {categories.map((category) => (
                    <div key={category.id} className="col-12 col-md-6 col-lg-4">
                        <MyAccountCard
                            as="a"
                            href={category.link} 
                            variant="page"
                            slug={`product-category-${category.id}`}
                            className="category-card text-decoration-none"
                            wrapContent={false}
                            style={{
                                display: 'block',
                                border: '1px solid var(--weka-subtle-border-color, #dee2e6)',
                                borderRadius: '0.375rem',
                                padding: '1rem',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'var(--weka-panel-bg, var(--weka-card-bg, #fff))',
                                height: '100%'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)';
                                e.currentTarget.style.borderColor = 'var(--weka-link-hover-color)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.borderColor = 'var(--weka-subtle-border-color, #dee2e6)';
                            }}
                        >
                            {category.image && (
                                <div 
                                    className="category-image mb-2"
                                    style={{
                                        width: '100%',
                                        height: '150px',
                                        overflow: 'hidden',
                                        borderRadius: '0.25rem',
                                        backgroundColor: 'var(--weka-subtle-bg, #f8f9fa)'
                                    }}
                                >
                                    <img 
                                        src={category.image.src} 
                                        alt={category.image.alt}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                            )}
                            <h6 
                                className="category-name mb-1"
                                style={{
                                    color: 'var(--weka-heading-color, #212529)',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    marginBottom: '0.25rem'
                                }}
                            >
                                {category.name}
                            </h6>
                            <p 
                                className="category-count text-muted mb-0"
                                style={{
                                    fontSize: '0.875rem',
                                    margin: 0
                                }}
                            >
                                {category.count} {category.count === 1 ? 'product' : 'products'}
                            </p>
                        </MyAccountCard>
                    </div>
                ))}
            </div>
        </MyAccountCard>
    );
};

export default ProductCategories;
